import { useState, useEffect } from "react";
import "./vendor.css";
import "./CRM.css"; // Reuse common styles
import { useNavigate } from "react-router-dom";
import dataService from "../services/dataService";
import authService from "../services/authService";
import Modal from "../components/Modal";
import { useToast } from "../context/ToastContext";
import { formatCurrency } from "../utils/formatCurrency";
import { Eye, FileEdit, Trash2, Search, Plus, Download } from "lucide-react";

function VendorScreen() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("view");
  const [formData, setFormData] = useState({});
  const [itemToDelete, setItemToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const { addToast } = useToast();

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const response = await dataService.getVendors();
      setVendors(response.data.data);
    } catch (error) {
      console.error("Failed to fetch vendors", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  const handleAddVendor = () => {
    navigate("/vendors/add");
  };

  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target.result;
      const rows = text.split('\n').map(row => row.split(','));
      
      const headers = rows[0].map(h => h.trim().replace(/"/g, ''));
      const nameIdx = headers.findIndex(h => h.toLowerCase().includes('name'));
      const emailIdx = headers.findIndex(h => h.toLowerCase().includes('email'));
      const phoneIdx = headers.findIndex(h => h.toLowerCase().includes('phone'));
      const addressIdx = headers.findIndex(h => h.toLowerCase().includes('street') || h.toLowerCase().includes('address'));

      const newVendors = [];
      for (let i = 1; i < rows.length; i++) {
        const row = rows[i];
        if (row.length < 2) continue;
        
        newVendors.push({
          name: (row[nameIdx] || 'Imported Vendor').replace(/"/g, '').trim(),
          email: (row[emailIdx] || '').replace(/"/g, '').trim(),
          phone: (row[phoneIdx] || '').replace(/"/g, '').trim(),
          address: (row[addressIdx] || '').replace(/"/g, '').trim(),
          status: 'Active',
          taxTreatment: 'VAT Registered'
        });
      }

      if (newVendors.length > 0) {
        addToast(`Importing ${newVendors.length} vendors...`, "success");
        try {
          for (const vend of newVendors) {
            await dataService.addVendor(vend);
          }
          fetchVendors();
          addToast("Import successful!", "success");
        } catch (err) {
          addToast("Error during import", "error");
        }
      }
    };
    reader.readAsText(file);
  };

  const handleView = (vendor) => {
    setSelectedVendor(vendor);
    setModalMode("view");
    setIsModalOpen(true);
  };

  const handleEdit = (vendor) => {
    navigate(`/vendors/edit/${vendor._id}`);
  };

  const handleDeleteClick = (vendor) => {
    setItemToDelete(vendor);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await dataService.deleteVendor(itemToDelete._id);
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
      fetchVendors();
      addToast("Vendor deleted successfully", "success");
    } catch (error) {
      addToast("Failed to delete vendor: " + error.message, "error");
    }
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    try {
      await dataService.updateVendor(selectedVendor._id, formData);
      setIsModalOpen(false);
      fetchVendors();
      addToast("Vendor updated successfully", "success");
    } catch (error) {
      addToast("Failed to update vendor: " + error.message, "error");
    }
  };

  return (
    <div className="crm-screen">
      <div className="screen-header">
        <div className="header-info">
          <h1>Vendor Management</h1>
          <p>Track your suppliers and purchase history</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <label className="secondary-btn" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Download size={18} /> Import CSV
            <input type="file" accept=".csv" onChange={handleImportCSV} style={{ display: 'none' }} />
          </label>
          {authService.hasPermission('vendor', 'editVendor') && (
            <button className="primary-btn" onClick={handleAddVendor}>
              <Plus size={18} /> Add New Vendor
            </button>
          )}
        </div>
      </div>

      <div className="summary-grid">
        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">Total Vendors</span>
            <h2 className="stat-value">{vendors.length}</h2>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">Total Purchases</span>
            <h2 className="stat-value">{formatCurrency(vendors.reduce((acc, v) => acc + (v.totalPurchases || 0), 0))}</h2>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">Pending Amount</span>
            <h2 className="stat-value" style={{ color: '#DC2626' }}>{formatCurrency(vendors.reduce((acc, v) => acc + (v.pendingAmount || 0), 0))}</h2>
          </div>
        </div>
      </div>

      <div className="content-card" style={{ marginTop: '32px' }}>
        <div className="card-controls">
          <h2 className="font-bold">Vendors</h2>
          <div className="search-box">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Search vendors by name, email, or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="table-wrapper">
          {loading ? (
            <p style={{ padding: '20px' }}>Loading vendors...</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Vendor Name</th>
                  <th>Contact Information</th>
                  <th>Total Purchases</th>
                  <th>Pending Amount</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {vendors
                  .filter(v =>
                    v.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    v.email.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                  .map((vendor) => (
                    <tr key={vendor._id}>
                      <td>
                        <div className="font-bold">{vendor.name}</div>
                        <div style={{ fontSize: '12px', color: '#6B7280' }}>ID: VEND-{vendor._id.substring(0, 6)}</div>
                      </td>
                      <td>
                        <div className="contact-cell">
                          <span className="email">{vendor.email}</span>
                          <span>{vendor.phone}</span>
                        </div>
                      </td>
                      <td>{vendor.totalPurchases || 0}</td>
                      <td><span className="font-bold">{formatCurrency(vendor.pendingAmount || 0)}</span></td>
                      <td>
                        <span className={`status-pill ${vendor.status.toLowerCase()}`}>
                          {vendor.status}
                        </span>
                      </td>
                      <td className="text-right">
                        <div className="action-icons">
                          <button className="icon-btn" title="View" onClick={() => handleView(vendor)}>
                            <Eye size={16} />
                          </button>

                          {authService.hasPermission('vendor', 'editVendor') && (
                            <button className="icon-btn success" title="Edit" onClick={() => handleEdit(vendor)}>
                              <FileEdit size={16} />
                            </button>
                          )}

                          {authService.hasPermission('vendor', 'deleteVendor') && (
                            <button className="icon-btn danger" title="Delete" onClick={() => handleDeleteClick(vendor)}>
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                {vendors.length === 0 && (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>No vendors found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={modalMode === "view" ? "Vendor Details" : "Edit Vendor"}
        footer={
          modalMode === "edit" ? (
            <>
              <button className="secondary-btn" onClick={() => setIsModalOpen(false)}>Cancel</button>
              <button className="primary-btn" onClick={handleUpdateSubmit}>Save Changes</button>
            </>
          ) : (
            <button className="primary-btn" onClick={() => setIsModalOpen(false)}>Close</button>
          )
        }
      >
        {modalMode === "view" && selectedVendor && (
          <div className="detail-view">
            <div className="detail-item">
              <span className="detail-label">Vendor Name</span>
              <span className="detail-value">{selectedVendor.name}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Email Address</span>
              <span className="detail-value">{selectedVendor.email}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Phone Number</span>
              <span className="detail-value">{selectedVendor.phone}</span>
            </div>
            <div className="detail-item full-width">
              <span className="detail-label">Address</span>
              <span className="detail-value">{selectedVendor.address}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Status</span>
              <span className={`status-pill ${selectedVendor.status?.toLowerCase()}`}>
                {selectedVendor.status}
              </span>
            </div>
          </div>
        )}

        {modalMode === "edit" && (
          <form className="modal-form" onSubmit={handleUpdateSubmit}>
            <div className="form-group">
              <label>Name</label>
              <input name="name" value={formData.name || ""} onChange={handleFormChange} required />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input name="email" value={formData.email || ""} onChange={handleFormChange} required />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input name="phone" value={formData.phone || ""} onChange={handleFormChange} />
            </div>
            <div className="form-group">
              <label>Address</label>
              <textarea name="address" value={formData.address || ""} onChange={handleFormChange}></textarea>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select name="status" value={formData.status || "Active"} onChange={handleFormChange}>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirm Deletion"
        footer={
          <>
            <button className="secondary-btn" onClick={() => setIsDeleteModalOpen(false)}>Cancel</button>
            <button className="primary-btn btn-danger" onClick={confirmDelete}>Delete Vendor</button>
          </>
        }
      >
        <div className="delete-confirm-view">
          <div className="delete-icon-wrapper">
            <Trash2 size={32} />
          </div>
          <h3>Are you sure?</h3>
          <p>You are about to delete <strong>{itemToDelete?.name}</strong>. This action cannot be undone and will remove the vendor and their purchase history.</p>
        </div>
      </Modal>
    </div>
  );
}

export default VendorScreen;